import { Button, Form, Input, message, Upload } from 'antd';
import { PlusOutlined, VideoCameraAddOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import './AddFileModal.scss';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import { filesApi } from '../../../api/files.api';
import { type File as InstallationFile } from '../../../gen/proto/api/gateway/v1/file_pb';
import SparkMD5 from 'spark-md5';

const ACCEPTED_MIME_TYPES = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,video/ogg,video/quicktime';

interface AddFileModalProps {
  installationUuid: string;
  onSuccess: (file: InstallationFile) => void;
}

const AddFileModal = ({ installationUuid, onSuccess }: AddFileModalProps) => {
  const { t } = useTranslation('installation-location', {
    keyPrefix: 'installationLocationDetail.installation.summary.files',
  });
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleClose = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedFile(null);
  };

  const handleUpload = async () => {
    try {
      const { name } = await form.validateFields();
      if (!selectedFile) {
        messageApi.error(t('noFileSelected'));
        return;
      }
      setUploading(true);
      const presignedUrl = await filesApi.createUploadUrl(installationUuid, name, selectedFile.type);

      await filesApi.uploadFile(presignedUrl, selectedFile);

      const arrayBuffer = await selectedFile.arrayBuffer();
      const checksum = SparkMD5.ArrayBuffer.hash(arrayBuffer);
      const objectKey = decodeURIComponent(new URL(presignedUrl).pathname.slice(1).split('/').slice(1).join('/'));

      const file = await filesApi.completeUploadRequest(
        objectKey,
        BigInt(selectedFile.size),
        checksum,
        name,
        installationUuid,
      );

      onSuccess(file);
      handleClose();
    } catch (e) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Button className={'Modal__button'} onClick={() => setIsModalOpen(true)} type="primary">
        <VideoCameraAddOutlined />
        {t('addFile')}
      </Button>
      <ModalCustom
        title={t('addImageVideo')}
        description={t('addImageVideoDesc')}
        isModalOpen={isModalOpen}
        setIsModalOpen={handleClose}
        footer={
          <Button type={'primary'} onClick={handleUpload} loading={uploading}>
            {t('add')}
          </Button>
        }
      >
        <Form form={form} layout="vertical" className={'Modal__formFile'}>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('image')} rules={[{ required: true }]}>
            <Upload
              listType="picture-card"
              showUploadList={false}
              accept={ACCEPTED_MIME_TYPES}
              beforeUpload={(file) => {
                setSelectedFile(file);
                return false;
              }}
            >
              {selectedFile ? (
                <span title={selectedFile.name}>
                  {selectedFile.name.length > 10 ? `${selectedFile.name.slice(0, 10)}...` : selectedFile.name}
                </span>
              ) : (
                <Button type="text" className="Modal__uploadTrigger">
                  <PlusOutlined />
                  <div>{t('upload')}</div>
                </Button>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </ModalCustom>
    </>
  );
};

export default AddFileModal;
